import {
  createFileRoute,
  Outlet,
  useNavigate,
  useParams,
  useRouterState,
} from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { AppShell, Sidebar, TopBar, NAV_ITEMS } from '@suvoda-uat/ui-suvoda';
import { TestCaseDetailView } from '../components/TestCaseDetailView.js';
import { currentUserName, signOut } from '../lib/auth.js';
import { type NavItem, visibleNavItems } from '../lib/nav.js';
import { createLifecycleBeforeLoad } from '../lib/lifecycle.js';

export const testCaseDetailBeforeLoad = createLifecycleBeforeLoad('/uat/cases/case');
export const isNestedEditRoutePath = (pathname: string): boolean => pathname.endsWith('/edit');

export const Route = createFileRoute('/uat/cases/$testId')({
  validateSearch: (search: Record<string, unknown>): { trialId: string } => ({
    trialId: typeof search['trialId'] === 'string' ? search['trialId'] : '',
  }),
  beforeLoad: testCaseDetailBeforeLoad,
  component: TestCaseDetailRoute,
});

export type TestCaseDetailRouteFrameProps = {
  readonly trialId: string;
  readonly testId: string;
  readonly isNestedEditRoute: boolean;
  readonly onBack: () => void;
  readonly onEditMode: () => void;
  /** Sidebar "Dashboard" destination — kept props-only so the frame stays router-free. */
  readonly onNavigateToDashboard?: () => void;
  /** Sidebar items — the route passes `visibleNavItems()` (Team is admin-only). */
  readonly navItems?: readonly NavItem[];
  /** Sidebar "Team" destination (feature 015), props-only like Dashboard. */
  readonly onNavigateToTeam?: () => void;
  readonly children?: ReactNode;
};

export function TestCaseDetailRouteFrame({
  trialId,
  testId,
  isNestedEditRoute,
  onBack,
  onEditMode,
  onNavigateToDashboard,
  navItems = NAV_ITEMS,
  onNavigateToTeam,
  children,
}: TestCaseDetailRouteFrameProps) {
  if (isNestedEditRoute) return <>{children}</>;

  return (
    <AppShell
      contentClassName="bg-surface-workspace p-[14px]"
      sidebar={
        <Sidebar
          items={[...navItems]}
          activeId="uat"
          onNavigate={(id) => {
            if (id === 'dashboard') onNavigateToDashboard?.();
            if (id === 'team') onNavigateToTeam?.();
          }}
          onSignOut={() => signOut()}
        />
      }
      topbar={<TopBar label="EXPAND" userName={currentUserName()} onSignOut={() => signOut()} />}
    >
      <TestCaseDetailView
        trialId={trialId}
        testId={testId}
        onBack={onBack}
        onEditMode={onEditMode}
      />
    </AppShell>
  );
}

function TestCaseDetailRoute() {
  const { testId } = useParams({ from: '/uat/cases/$testId' });
  const { trialId } = Route.useSearch();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  return (
    <TestCaseDetailRouteFrame
      trialId={trialId}
      testId={testId}
      isNestedEditRoute={isNestedEditRoutePath(pathname)}
      navItems={visibleNavItems()}
      onNavigateToDashboard={() => void navigate({ to: '/' })}
      onNavigateToTeam={() => void navigate({ to: '/team' })}
      onBack={() => navigate({ to: '/uat', search: { trialId } })}
      // F-032: editing is now inline on the detail surface (Block Mode ↔ Free
      // Text) — there is no separate Edit Mode route. `onEditMode`'s PRESENCE
      // signals editing-availability to the detail view; it performs no
      // navigation. (Server still enforces the editor-tier on save.)
      onEditMode={() => {}}
    >
      <Outlet />
    </TestCaseDetailRouteFrame>
  );
}
